import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import {
  audioElementAtom,
  currentTrackIdAtom,
} from '@/features/AudioPlayer/atoms';

import {
  resetVideoAtom,
  videoElementAtom,
  videoFileAtom,
} from '../atoms/videoPlayerAtoms';

/**
 * Syncs video playback with audio
 * - Plays/pauses video in sync with audio when video is loaded
 * - Loops video from reset point when it ends
 * - Resets video when track changes or audio starts playing
 */
export function useVideoAudioSync() {
  const audioElement = useAtomValue(audioElementAtom);
  const videoElement = useAtomValue(videoElementAtom);
  const videoFile = useAtomValue(videoFileAtom);
  const resetVideo = useSetAtom(resetVideoAtom);
  const currentTrackId = useAtomValue(currentTrackIdAtom);

  const prevTrackIdRef = useRef(currentTrackId);

  useEffect(() => {
    // Only sync if both audio and video elements exist and a video is loaded
    if (!(audioElement && videoElement && videoFile)) {
      prevTrackIdRef.current = currentTrackId;
      return;
    }

    const trackChanged =
      currentTrackId !== prevTrackIdRef.current && currentTrackId !== null;

    if (trackChanged) {
      resetVideo();
      if (!audioElement.paused) {
        videoElement.play().catch(() => {
          // Ignore autoplay errors
        });
      }
    }

    const handleAudioPlaying = () => {
      resetVideo();
      videoElement.play().catch(() => {
        // Ignore autoplay errors - video will play once user interacts
      });
    };

    const handleAudioPause = () => {
      videoElement.pause();
    };

    const handleVideoEnded = () => {
      resetVideo();
      // If audio is still playing, restart the video
      if (!audioElement.paused) {
        videoElement.play().catch(() => {
          // Ignore autoplay errors
        });
      }
    };

    audioElement.addEventListener('playing', handleAudioPlaying);
    audioElement.addEventListener('pause', handleAudioPause);
    videoElement.addEventListener('ended', handleVideoEnded);

    prevTrackIdRef.current = currentTrackId;

    return () => {
      audioElement.removeEventListener('playing', handleAudioPlaying);
      audioElement.removeEventListener('pause', handleAudioPause);
      videoElement.removeEventListener('ended', handleVideoEnded);
    };
  }, [audioElement, videoElement, videoFile, currentTrackId, resetVideo]);
}
